<template>
  <!--
    Announcement card (new channel layout).

    Presentational card that sits between the hero panel / login CTA and the
    QUESTS rail. When the host (channel.vue) has at least one thread it passes
    the thread's content split as :title / :description; otherwise it falls back
    to static i18n copy. The carousel dots are driven by :dot-count / :active-index
    props so the host can reflect the real thread list length (capped at 4).
    Clicking the card emits "click" so the host can open the ANNOUNCEMENTS modal.
  -->
  <button
    type="button"
    data-testid="channel-benefits-bar"
    class="mx-5 w-[calc(100%-2.5rem)] flex flex-row items-end justify-between gap-4 rounded-[20px] bg-surface-secondary p-4 text-left transition-colors hover:bg-surface-secondary-hover desktop:p-6"
    @click="emit('click')"
  >
    <!--
      Two responsive layouts share the same data (Figma desktop 18681:20707,
      tablet 18681:36446, mobile 18681:37187):

      - Desktop / tablet (>= 820px): the card is a flex row, items-end. The left
        column stacks the title (19px) over the description (16px); the dots sit
        bottom-right. The `desktop:` dot block is shown, the inline mobile one
        hidden.
      - Mobile (< 820px): the card is still a row but the left column is flex-1
        and holds the title (16px, full width) above a description+dots row. The
        inline dot block is shown, the desktop one hidden.

      The dot block is rendered in both positions (it is tiny, presentational and
      aria-hidden) so each breakpoint can place it correctly without a brittle
      single-DOM flex trick.
    -->
    <div class="flex min-w-0 flex-1 flex-col gap-2">
      <span
        class="text-base font-bold uppercase leading-[22px] text-text desktop:text-[19px]"
      >
        {{ title ?? t("channel.benefitsBar.title") }}
      </span>

      <div class="flex flex-row items-center justify-between gap-4">
        <span class="text-base font-medium leading-[22px] text-text">
          {{ description ?? t("channel.benefitsBar.description") }}
        </span>

        <!-- Mobile dots: inline with the description, hidden from 820px up. -->
        <div
          class="flex shrink-0 flex-row items-center gap-2 py-1 desktop:hidden"
          aria-hidden="true"
        >
          <span
            v-for="i in dotCount"
            :key="i"
            :class="[
              'h-2 rounded-full',
              i - 1 === activeIndex ? 'w-10 bg-fill-brand' : 'w-2 bg-surface',
            ]"
          ></span>
        </div>
      </div>
    </div>

    <!-- Desktop/tablet dots: bottom-right of the card, hidden below 820px. -->
    <div
      class="hidden shrink-0 flex-row items-center gap-2 py-1 desktop:flex"
      aria-hidden="true"
    >
      <span
        v-for="i in dotCount"
        :key="i"
        :class="[
          'h-2 rounded-full',
          i - 1 === activeIndex ? 'w-10 bg-fill-brand' : 'w-2 bg-surface',
        ]"
      ></span>
    </div>
  </button>
</template>

<script setup lang="ts">
import { useI18n } from "vue-i18n";

withDefaults(
  defineProps<{
    /** Optional override for the card title; falls back to the i18n default. */
    title?: string;
    /** Optional override for the card description; falls back to the i18n default. */
    description?: string;
    /**
     * Number of carousel dots to render (capped at 4 by the host — one per
     * thread, reflecting the thread list length). Defaults to 4 (the static
     * Figma design shows four dots).
     */
    dotCount?: number;
    /**
     * Zero-based index of the active (wider, brand-coloured) dot. Defaults to 0
     * (the first/latest thread is always shown on open).
     */
    activeIndex?: number;
  }>(),
  {
    dotCount: 4,
    activeIndex: 0,
  },
);

const emit = defineEmits<{
  (e: "click"): void;
}>();

const { t } = useI18n();
</script>
